import { Module } from 'vuex'
import axios from '@/plugins/axios'
import { sortResults, sortCollection, sortCollectionWithArray } from '@/utils/collections'

export interface Topic {
    username: string
    token: string
}

const topicModule: Module<any, any> = {
    actions: {
        async initTopicIndex() {
            if (this.topicIndexLoading) {
        		await this.initTopicPromise
        		return
        	}
            this.topicIndexLoading = true
            this.initTopicPromise = this.dispatch('initIndex', {
                id: 'topic',
                idField: 'slug',
                indexedFields: ['name'],
                source: '/api/topics',
                refresh: true,
            })
            await this.initTopicPromise
            this.topicIndexLoading = false
            this.initTopicPromise = null
        },
        
        async getTopics(_, params) {
            const indexExists = await this.dispatch('indexExists', 'topic')
            if (!indexExists || params?.refresh) {
                await this.dispatch('initTopicIndex')
            }

            const slugsFilter = (items) => {
                const filter = params.slugs
                return filter
                    ? items?.filter((e) => filter.includes(e.slug))
                    : items
            }

            const sortMembers = (items) => {
                return sortCollectionWithArray(items, 'members', params?.sortOrder?.toLowerCase())
            }

            const topicTypeFilter = (items) => {
                const filter = params.topicType
                return filter
                    ? items?.filter((e) => e.private === (filter==='private'))
                    : items
            }

            const startDateFilter = (items) => {
                const filter = params.start
                return filter
                    ? items?.filter((e) => !e.createdAt || e.createdAt <= filter)
                    : items
            }

            const endDateFilter = (items) => {
                const filter = params.end
                return filter
                    ? items?.filter((e) => !e.createdAt || e.createdAt >= filter)
                    : items
            }

            const sortName = (items) => {
                return sortCollection(items, 'name', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortTopicType = (items) => {
                return sortResults(items, 'private', params?.sortOrder)
            }
            
            const sortPosts = (items) => {
                return sortCollection(items, 'stats.postsDelivered', params?.sortOrder?.toLowerCase() === 'asc')
            }
            
            const sortEngagements = (items) => {
                return items?.sort((a, b) =>
                a?.stats.likes + a?.stats.comments + a?.stats.reshares < b?.stats.likes + b?.stats.comments + b?.stats.reshares
                    ? params?.sortOrder?.toLowerCase() === 'asc' ? -1 : 1
                    : a?.stats.likes + a?.stats.comments + a?.stats.reshares > b?.stats.likes + b?.stats.comments + b?.stats.reshares
                    ? params?.sortOrder?.toLowerCase() === 'asc' ? 1 : -1
                    : 0,
                )
            }
            
            const sortClicks = (items) => {
                return sortCollection(items, 'stats.clicks', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortMediaPoints = (items) => {
                return sortCollection(items, 'mediaPoints', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortContentCount = (items) => {
                return sortCollection(items, 'contentCount', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const contentSourceFilter = (items) => {
                return sortResults(items, 'feeds', params?.sortOrder)
            }

            const createdAtFilter = (items) => {
                return sortResults(items, 'createdBy', params?.sortOrder)
            }

            const getSortOrder = () => {                
                if (params?.sortBy === 'members') {
                    return sortMembers
                } else if (params?.sortBy === 'type') {
                    return sortTopicType
                } else if (params?.sortBy === 'posts') {
                    return sortPosts
                } else if (params?.sortBy === 'engagements') {
                    return sortEngagements
                } else if (params?.sortBy === 'clicks') {
                    return sortClicks
                } else if (params?.sortBy === 'media_points') {
                    return sortMediaPoints
                } else if (params?.sortBy === 'content_count') {
                    return sortContentCount
                } else if (params?.sortBy === 'content_sources'){
                    return contentSourceFilter
                } else if (params?.sortBy === 'createdAt'){
                    return createdAtFilter
                } else {
                    return sortName
                }
            }

            let records = null
            records = await this.dispatch('getRecords', {
                index: 'topic',
                term: params?.search,
                fields: ['name'],
                page: params?.page ?? 1,
                perPage: params?.perPage ?? 10,
                filters: [slugsFilter, topicTypeFilter, startDateFilter, endDateFilter],
                sort: getSortOrder(),
            })

            return records
        },

        async getTopicsFresh(_, params){
            let data = null
            await axios.get('/api/topics',{params}).then((response) => {
                data = response.data
            })

            return data
        },

        async getTopic(_, id) {
            if (id === undefined || id === null) {
                throw new Error(`Invalid Topic ID specified: ${id}`)
            }

            const indexExists = await this.dispatch('indexExists', 'topic')

            if (indexExists) {
                return await this.dispatch('getRecord', {
                    index: 'topic',
                    id,
                })
            } else {
                return await this.dispatch('getTopicFresh', id)
            }
        },

        async getTopicFresh(_, id) {
            const indexExists = await this.dispatch('indexExists', 'topic')

            const { data } = await axios.get(`/api/topics/${id}`)
            if (!indexExists) {
                await this.dispatch('initTopicIndex')
            }

            if (data) {
                await this.dispatch('addRecord', {
                    index: 'topic',
                    record: data,
                })
            }

            return data
        },

        async setTopicContentCount(_, params) {
            if (params.id === undefined || params.id === null) {
                throw new Error(`Invalid Topic ID specified: ${params.id}`)
            }

            const indexExists = await this.dispatch('indexExists', 'topic')

            if (indexExists) {
                const topic = await this.dispatch('getRecord', {
                    index: 'topic',
                    id: params.id,
                })
                if (topic) {
                    if (params.value) {
                        if (params?.status==='active') {
                            topic.contentCountByStatus.active = params.value
                        } else if (params?.status==='inreview') {
                            topic.contentCountByStatus.inreview = params.value
                        } else if (params?.status==='draft') {
                            topic.contentCountByStatus.draft = params.value
                        }                        
                    } else {
                        let hasValue = false
                        if (params?.status==='active' ) {
                            hasValue = topic.contentCountByStatus?.active !== null && topic.contentCountByStatus?.active !== undefined
                        } else if (params?.status==='inreview') {
                            hasValue = topic.contentCountByStatus?.inreview !== null && topic.contentCountByStatus?.inreview !== undefined
                        } else if (params?.status==='draft') {
                            hasValue = topic.contentCountByStatus?.draft !== null && topic.contentCountByStatus?.draft !== undefined
                        }
                        if (!hasValue) {
                            const topicId = params?.id ? '&topics=' + params?.id : ''
                            const contentStatus = params?.status ? '&status=' + params?.status : ''
                            const excludeSharedBy = params?.excludeSharedBy ? '&excludeSharedByWorkspaceMemberId=' + params?.excludeSharedBy : ''
                            await axios.get(`/api/content?page=1&itemPerPage=1${topicId}${contentStatus}${excludeSharedBy}`)
                            .then((response) => {
                                topic.contentCountByStatus = {
                                    active: params?.status==='active' ? response.data['hydra:totalItems'] : (topic.contentCountByStatus?.active ?? null),
                                    inreview: params?.status==='inreview' ? response.data['hydra:totalItems'] : (topic.contentCountByStatus?.inreview ?? null),
                                    draft: params?.status==='draft' ? response.data['hydra:totalItems'] : (topic.contentCountByStatus?.draft ?? null),
                                }    
                            })
                        }
                    }         
                    return topic
                } else {
                    throw new Error(`Topic not found: ${params.id}`)
                }
            }
        },

        async createTopic(_, params) {
            await axios.post('/api/topics', params)
        },

        async editTopic(_, params) {
            await axios.put(`/api/topics/${params.slug}`, params)
        },

        async deleteTopic(_, params) {
            await axios.delete(`/api/topics/${params.slug}`)
        },

        async addTopicFeed(_, params) {
            await axios.put(`/api/topics/${params.slug}/addfeed`, params)
        },

        async removeTopicFeed(_, params) {
            await axios.put(`/api/topics/${params.slug}/removefeed`, params)
        },

        async addTopicMember(_, params) {
            await axios.put(`/api/topics/${params.slug}/addmember`, params)
        },

        async removeTopicMember(_, params) {
            await axios.put(`/api/topics/${params.slug}/removemember`, params)
        },
    },
}

export default topicModule
